import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import type { ToastItem, ToastOptions, ToastPosition, ToastPromiseMessages } from './toast.models';

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly MAX_TOASTS = 5;
  private readonly DEFAULT_DURATION = 4000;
  private readonly DEFAULT_POSITION: ToastPosition = 'top-right';

  private readonly _toasts = signal<ToastItem[]>([]);
  readonly toasts = this._toasts.asReadonly();

  show(options: ToastOptions): string {
    const id = this.generateId();
    const toast: ToastItem = {
      id,
      type: options.type ?? 'default',
      title: options.title ?? '',
      message: options.message,
      duration: options.duration ?? this.DEFAULT_DURATION,
      position: options.position ?? this.DEFAULT_POSITION,
      action: options.action ?? (null as unknown as ToastItem['action']),
      dismissible: options.dismissible ?? true,
      createdAt: Date.now(),
      isLeaving: false,
    };

    this._toasts.update(current => {
      const visible = current.filter(t => !t.isLeaving);
      if (visible.length >= this.MAX_TOASTS) {
        this.dismiss(visible[0].id);
      }
      return [...current, toast];
    });

    if (toast.duration > 0) {
      setTimeout(() => this.dismiss(id), toast.duration);
    }

    return id;
  }

  success(options: Omit<ToastOptions, 'type'>): string {
    return this.show({ ...options, type: 'success' });
  }

  error(options: Omit<ToastOptions, 'type'>): string {
    return this.show({ ...options, type: 'error' });
  }

  warning(options: Omit<ToastOptions, 'type'>): string {
    return this.show({ ...options, type: 'warning' });
  }

  info(options: Omit<ToastOptions, 'type'>): string {
    return this.show({ ...options, type: 'info' });
  }

  promise<T>(source$: Observable<T>, messages: ToastPromiseMessages): Observable<T> {
    const loadingId = this.show({
      type: 'default',
      message: messages.loading,
      duration: 0,
      dismissible: false,
    });

    return new Observable<T>(subscriber => {
      const sub = source$.subscribe({
        next: (value) => {
          this.dismiss(loadingId);
          const msg = typeof messages.success === 'function'
            ? messages.success(value)
            : messages.success;
          this.success({ message: msg });
          subscriber.next(value);
          subscriber.complete();
        },
        error: (err) => {
          this.dismiss(loadingId);
          const msg = typeof messages.error === 'function'
            ? messages.error(err)
            : messages.error;
          this.error({ message: msg });
          subscriber.error(err);
        },
      });
      return () => sub.unsubscribe();
    });
  }

  dismiss(id: string): void {
    this._toasts.update(toasts =>
      toasts.map(t => t.id === id ? { ...t, isLeaving: true } : t)
    );
    setTimeout(() => {
      this._toasts.update(toasts => toasts.filter(t => t.id !== id));
    }, 320);
  }

  dismissAll(): void {
    this._toasts().map(t => t.id).forEach(id => this.dismiss(id));
  }

  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }
}
